import React, { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import BaseModal from "../../components/BaseModal";
import BaseButton from "../../components/BaseButton";
import BaseIcon from "../../components/BaseIcon";
import { MODAL_TYPES } from "../../utils/emitter";
import useModalActions from "../../hooks/useModalActions";
import Pricing from "../../store/Pricing";
import AnketaDescriptionBody from "../../components/AnketaDescriptionBody";
import Points from "../../store/Points";
import PersonalAccount from "../../store/PersonalAccount";
import ROUTES from "../../utils/routes";
import "./style.scss";

// Универсальный попап покупки (ТЗ Этап 2 п.6.4): 2 кнопки — деньги / баллы + крестик.
// payload: { productType: GAME|REPEAT_TEST|BLANK_FORM|EXTENDED_57, gameId?, from?,
//            onPurchased?, descriptionKey? — i18n-ключ вместо стандартного описания товара }
// Если баллов достаточно — обе кнопки залиты (сиреневые); при нехватке кнопка баллов
// ведёт в попап «не хватает баллов».
export default observer(function BuyProductModal() {
  const [payload, setPayload] = useState(null);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { openModal, closeModal } = useModalActions();

  useEffect(() => {
    Pricing.fetch();
  }, []);

  // баланс нужен, чтобы решить, залита ли кнопка баллов и хватает ли их (без этого
  // null трактовался как 0 → ложное «не хватает»). Грузим при КАЖДОМ открытии, а не
  // один раз на mount: fetchBalance с пустым catch мог упасть и оставить null навсегда
  const onModalPayload = (data) => {
    if (!data) return;
    setPayload(data);
    if (Points.balance === null) Points.fetchBalance();
  };

  const productType = payload?.productType;
  const price = productType ? Pricing.get(productType) : null;
  const canPayWithPoints = Boolean(price && price.points !== null);
  // отличаем «баланс ещё не загружен» от «баллов реально мало»
  const balanceLoaded = Points.balance !== null;
  const enoughPoints =
    canPayWithPoints && balanceLoaded && Points.balance >= price.points;

  // prod по умолчанию = основной товар попапа; параметр нужен для апселл-строки
  // (в попапе «Тест Wepurplay» две строки покупки: REPEAT_TEST и полная игра GAME).
  const buyWithMoney = (prod = productType) => {
    closeModal(MODAL_TYPES.BUY_PRODUCT);
    navigate(ROUTES.PAYMENT_PAGE, {
      state: {
        productType: prod,
        gameId: payload?.gameId,
        from: payload?.from,
      },
    });
  };

  const buyWithPoints = (prod = productType) => {
    // баланс ещё грузится — не уводим в «не хватает баллов», просто ждём
    if (!balanceLoaded) return;
    const p = Pricing.get(prod);
    const enough = p && p.points !== null && Points.balance >= p.points;
    if (!enough) {
      closeModal(MODAL_TYPES.BUY_PRODUCT);
      openModal(MODAL_TYPES.NOT_ENOUGH_POINTS, { productType: prod });
      return;
    }
    // «покупка игры» = апгрейд демо: шлём DEMO_GAME, зеркально денежному пути
    // PaymentPage (бэк тоже нормализует, но не полагаемся на одну сторону)
    Points.spend(
      prod === "GAME" ? "DEMO_GAME" : prod,
      { gameId: payload?.gameId },
      async () => {
        closeModal(MODAL_TYPES.BUY_PRODUCT);
        await PersonalAccount.getPersonalAccountData();
        payload?.onPurchased && payload.onPurchased();
      },
      (error) => {
        closeModal(MODAL_TYPES.BUY_PRODUCT);
        const code = error?.response?.data?.message?.code;
        if (code === "INSUFFICIENT_POINTS") {
          openModal(MODAL_TYPES.NOT_ENOUGH_POINTS, { productType });
        } else if (code === "CONNECT_REQUIRED") {
          // +57/анкета без коннектнутой игры (ТЗ C2/D1): вместо молчаливого
          // закрытия — попап «Создать тест» (создай игру и пригласи партнёра)
          openModal(MODAL_TYPES.CREATE_TEST_INFO);
        } else if (code === "FULFILLMENT_FAILED") {
          // доставка не удалась, баллы уже возвращены (REFUND) — объясняем юзеру,
          // иначе модалка молча закрывалась и списание выглядело потерей баллов
          openModal(MODAL_TYPES.PURCHASE_FAILED);
        }
      }
    );
  };

  // Пустая анкета (Figma 11700-46988): в попапе покупки — полная версия текста
  // «Что такое чистая анкета?» с рамкой «Как это работает?» и секцией «А если
  // партнер откажется заполнять?». Тексты — blank_form.details (те же, что в
  // попапе ПОДРОБНЕЕ 7-й зоны); акценты и рамка размечаются по структуре абзацев.
  const blankParagraphs =
    productType === "BLANK_FORM"
      ? t("blank_form.details.paragraphs", { returnObjects: true })
      : null;
  const blankDescription = Array.isArray(blankParagraphs) ? (
    <span className="buy-product-modal__blank-details">
      {blankParagraphs.map((p, i) => {
        const isAccent = i === 2 || i === blankParagraphs.length - 1;
        const isBox = i === 3;
        const isSection = i === 4;
        if (isBox || isSection) {
          const [head, ...rest] = p.split("\n");
          return (
            <span
              key={i}
              className={
                isBox
                  ? "buy-product-modal__blank-box"
                  : "buy-product-modal__blank-section"
              }
            >
              <span className="buy-product-modal__blank-head">{head}</span>
              <span>{rest.join("\n")}</span>
            </span>
          );
        }
        return (
          <span
            key={i}
            className={
              isAccent
                ? "buy-product-modal__blank-accent"
                : "buy-product-modal__blank-p"
            }
          >
            {p}
          </span>
        );
      })}
      <span className="buy-product-modal__blank-unlock">
        {t("blank_form.details.unlock_line")}
      </span>
    </span>
  ) : null;

  // item 4: покупка «Анкеты Откровений» = покупка полной игры (GAME/$49), но с
  // Анкета-специфичным текстом (а не «весь тест»). Триггеры передают variant:"anketa".
  const isAnketa = payload?.variant === "anketa";
  const anketaIncludes = isAnketa
    ? t("loyalty.products.ANKETA.includes", {
        returnObjects: true,
        priceTest: Pricing.format("REPEAT_TEST"),
        priceExt: Pricing.format("EXTENDED_57"),
        priceFull: Pricing.format("GAME"),
      })
    : null;
  const anketaDescription = isAnketa ? (
    <span className="buy-product-modal__anketa">
      <span className="buy-product-modal__anketa-lead">
        {t("loyalty.products.ANKETA.lead")}
        {/* сиреневый акцент — с новой строки (по Figma 15645-50998/50977) */}
        <br />
        <span className="buy-product-modal__anketa-lead-accent">
          {t("loyalty.products.ANKETA.lead_accent")}
        </span>
      </span>
      <span className="buy-product-modal__anketa-includes-title">
        {t("loyalty.products.ANKETA.includes_title")}
      </span>
      <span className="buy-product-modal__anketa-list">
        {(Array.isArray(anketaIncludes) ? anketaIncludes : []).map((line, i) => (
          <span key={i} className="buy-product-modal__anketa-item">
            {line}
          </span>
        ))}
      </span>
      <span className="buy-product-modal__anketa-note">
        {t("loyalty.products.ANKETA.note", { priceFull: Pricing.format("GAME") })}
      </span>
    </span>
  ) : null;

  // 21.09 (решение клиента): вариант anketa_full — ПОЛНЫЙ текст описания анкеты
  // (тот же, что на странице /insight-description, минус блок «Инструкция») и
  // внизу кнопки деньги/баллы. Показывается по клику на карточку и на «Показать
  // все карточки» в «Выбрать тему», когда игра не куплена.
  const isAnketaFull = payload?.variant === "anketa_full";
  // 24.09: заголовок рендерим ВНУТРИ описания, а не через title у BaseModal.
  // Две причины: у .base-modal__title жёсткий max-width 376px, из-за которого
  // «Описание анкеты» ломалось на две строки (в макете — одна), и шапка модалки
  // не прокручивается, поэтому заголовок висел над уезжающим текстом.
  const anketaFullDescription = isAnketaFull ? (
    <span className="buy-product-modal__anketa-full">
      <span className="buy-product-modal__anketa-full-title neon-text">
        {t("insight_form_new_description_page.title")}
      </span>
      <AnketaDescriptionBody variant="modal" />
    </span>
  ) : null;

  // Task 3: вариант «Игра Wepurplay» (game_full) — описание игры + 49$/4900.
  // Попап «Тест Wepurplay» (2 тарифа + «Обрати внимание!») — отдельный SeeFullTestModal.
  const isGameFull = payload?.variant === "game_full";

  // строка покупки (деньги + баллы) для конкретного товара — переиспользуется в апселле
  const buyRow = (prod) => {
    const p = Pricing.get(prod);
    const canPts = Boolean(p && p.points !== null);
    const enough = canPts && balanceLoaded && Points.balance >= p.points;
    return (
      <div className="buy-product-modal__actions">
        <BaseButton onClick={() => buyWithMoney(prod)}>
          {t("loyalty.buy_for", { price: Pricing.format(prod) })}
        </BaseButton>
        {canPts ? (
          <BaseButton
            onClick={() => buyWithPoints(prod)}
            unfilledGradient={enough}
            unfilledGray={!enough}
            disabled={!enough || Points.isSpending || !balanceLoaded}
          >
            <span className="buy-product-modal__points">
              <BaseIcon
                name="star"
                className={`buy-product-modal__points-star${
                  enough ? "" : " buy-product-modal__points-star--muted"
                }`}
              />
              {p.points}
            </span>
          </BaseButton>
        ) : null}
      </div>
    );
  };

  // game_full: полное описание игры + строка «Разблокируй…»
  const gameFullDescription = isGameFull ? (
    <span className="buy-product-modal__gamefull">
      <span className="buy-product-modal__gamefull-desc">
        {t("loyalty.game_full.desc")}
      </span>
      <span className="buy-product-modal__gamefull-unlock">
        {t("loyalty.game_full.unlock")}
      </span>
    </span>
  ) : null;

  return (
    <BaseModal
      className={`buy-product-modal${
        isAnketa ? " buy-product-modal--anketa" : ""
      }${isAnketaFull ? " buy-product-modal--anketa-full" : ""}`}
      eventTypeModal={MODAL_TYPES.BUY_PRODUCT}
      onPayload={onModalPayload}
      title={
        isGameFull || isAnketa || isAnketaFull
          ? ""
          : productType
          ? t(`loyalty.products.${productType}.title`)
          : ""
      }
      description={
        isGameFull
          ? gameFullDescription
          : isAnketaFull
          ? anketaFullDescription
          : isAnketa
          ? anketaDescription
          : productType
          ? blankDescription ||
            t(payload?.descriptionKey || `loyalty.products.${productType}.description`)
          : ""
      }
      descriptionStyle="buy-product-modal__description"
    >
      {isAnketaFull ? (
        // 22.09: клиент попросил сохранить в попапе нижнюю строку со страницы
        // («…необходимо приобрести полную версию игры») — она под кнопками
        <div className="buy-product-modal__anketa-full-footer">
          <span className="buy-product-modal__anketa-full-unlock">
            {t("loyalty.products.GAME.description")}
          </span>
          {buyRow(productType)}
          <span className="buy-product-modal__anketa-full-note">
            {t("insight_form_description_page.toUse")}
          </span>
        </div>
      ) : isAnketa ? (
        // note2 «Это выгоднее…» — ПОД кнопками (по Figma 15645-50998/50977)
        <div className="buy-product-modal__anketa-footer">
          {buyRow(productType)}
          <span className="buy-product-modal__anketa-note2">
            {t("loyalty.products.ANKETA.note2")}
          </span>
        </div>
      ) : productType ? (
        buyRow(productType)
      ) : null}
    </BaseModal>
  );
});
